import { ClassData } from "../dtos/classes.type";
import { DEFAULT_CATEGORY } from "../services/default-category.service";
import { URL_PARAMETERS } from "./const.util";
import { getUrlParameterValue } from "./url.util";

export function filterByGender(data: ClassData[]): ClassData[] {
  const gender = getUrlParameterValue(URL_PARAMETERS.GENDER);

  if (gender === "All") {
    return data;
  }

  return data.filter(
    (item) => item?.gender?.toLowerCase() === gender || item.gender === "All"
  );
}

export function filterByKeyAndValue(
  data: ClassData[],
  key: string,
  value: string
): ClassData[] {
  const normalizedValue = value?.toLowerCase();
  if (
    normalizedValue === "all" ||
    normalizedValue === "null" ||
    !value ||
    key === URL_PARAMETERS.FUNNEL_SKIP
  ) {
    return data;
  }

  return data.filter(
    (item: any) =>
      item[key]?.toLowerCase() === normalizedValue ||
      item[key]?.toLowerCase() === "all" ||
      item[key]?.toLowerCase() === "null"
  );
}

export function filterByFullClasses(data: ClassData[]): ClassData[] {
  const hasFullClasses =
    getUrlParameterValue(URL_PARAMETERS.FULL_CLASSES) === "true"; // complete this
  if (!hasFullClasses) {
    return data;
  }

  return data.filter((item) => item?.openings.calculated_openings > 0);
}

export function filterByAge(data: ClassData[]): ClassData[] {
  const ageFilter = getUrlParameterValue(URL_PARAMETERS.AGE);
  if (!ageFilter) {
    return data;
  }

  const ageRange = getAgeRangeFromFilterValue(ageFilter);
  if (!ageRange) {
    return data;
  }
  const [filterMinAge, filterMaxAge] = ageRange;

  return data.filter((item) => {
    const itemMinAge = item.min_age ? convertDurationToMonths(item.min_age) : 0;
    const itemMaxAge = item.max_age
      ? convertDurationToMonths(item.max_age)
      : Infinity;

    return itemMinAge <= filterMaxAge && itemMaxAge >= filterMinAge;
  });
}

function convertDurationToMonths(duration: string): number {
  const matches = duration?.match(/P(?:(\d+)Y)?(?:(\d+)M)?/);
  if (!matches) return 0;

  const years = parseInt(matches[1] || "0", 10);
  const months = parseInt(matches[2] || "0", 10);

  return years * 12 + months;
}

function getAgeRangeFromFilterValue(
  ageFilter: string
): [number, number] | null {
  const normalized = ageFilter.trim().toLowerCase();
  if (
    !normalized ||
    normalized === "all" ||
    normalized === "all selected" ||
    normalized === "null"
  ) {
    return null;
  }

  const isoRangeParts = ageFilter.split("-");
  if (
    isoRangeParts.length === 2 &&
    isoRangeParts[0].startsWith("P") &&
    isoRangeParts[1].startsWith("P")
  ) {
    return [
      convertDurationToMonths(isoRangeParts[0]),
      convertDurationToMonths(isoRangeParts[1]),
    ];
  }

  const underMonths = normalized.match(/^under\s+(\d+)\s+months?$/);
  if (underMonths) {
    const maxMonths = parseInt(underMonths[1], 10);
    return [0, maxMonths];
  }

  const monthsToYears = normalized.match(
    /^(\d+)\s+months?\s+to\s+(\d+)\s+years?$/
  );
  if (monthsToYears) {
    const minMonths = parseInt(monthsToYears[1], 10);
    const maxMonths = parseInt(monthsToYears[2], 10) * 12;
    return [minMonths, maxMonths];
  }

  const yearsToYears = normalized.match(/^(\d+)\s+years?\s+to\s+(\d+)\s+years?$/);
  if (yearsToYears) {
    const minMonths = parseInt(yearsToYears[1], 10) * 12;
    const maxMonths = parseInt(yearsToYears[2], 10) * 12;
    return [minMonths, maxMonths];
  }

  const overYears = normalized.match(/^over\s+(\d+)\s+years?$/);
  if (overYears) {
    const minMonths = parseInt(overYears[1], 10) * 12;
    return [minMonths, Infinity];
  }

  const exactYears = normalized.match(/^(\d+)\s+years?$/);
  if (exactYears) {
    const minMonths = parseInt(exactYears[1], 10) * 12;
    return [minMonths, minMonths + 11];
  }

  return null;
}

export function filterByMultipleValues(
  data: ClassData[],
  list: string
): ClassData[] {
  if (typeof list !== "string" || list.trim() === "") {
    return [];
  }

  const filters = list.split(",").map((item) => item.trim());
  let allData = [];
  for (const index in filters) {
    allData.push(
      ...data.filter((item) => item[DEFAULT_CATEGORY] === filters[index])
    );
  }

  return allData;
}

export function filterByDays(data: ClassData[], list: string): ClassData[] {
  if (typeof list !== "string" || list.trim() === "") {
    return [];
  }

  const filters = list.split(",").map((item) => item.trim());
  let allData = [];
  for (const index in filters) {
    allData.push(
      ...data.filter((item: any) => item.meeting_days?.[`${filters[index]}`])
    );
  }

  return allData;
}

export function filterByLocation(data: ClassData[]): ClassData[] {
  const location = getUrlParameterValue(URL_PARAMETERS.LOCATION);
  return data.filter((item) => item.location_name === location);
}

export function filterBySession(
  data: ClassData[],
  sessionTargetName: string
): ClassData[] {
  if (!sessionTargetName) return data;
  const target = sessionTargetName.toLowerCase();
  return data.filter((item) => item.session?.toLowerCase() === target);
}
